import { useEffect, useMemo, useState } from "react";
import { Box, Flex, Text } from "@storyofams/react-ui";
import { AnimatePresence, m } from "framer-motion";
import { useHistory } from "react-router-dom";

import { BackButton, ProgressBar } from "~/components";
import config from "~/config";
import { FlowNodeType, SessionStatus } from "~/graphql/api/sdk";
import { useFlow, useProgress, useSession } from "~/hooks";

import { EmailForm } from "./EmailForm";
import { QuestionForm } from "./QuestionForm";
import { InputOneLineTextForm } from "./InputOneLineTextForm";
import { InputMultiLineTextForm } from "./InputMultiLineTextForm";
import { TransitionScreen } from "./TransitionScreen";

const Question = ({
  match: {
    params: { number },
  },
}) => {
  const { flow, slug, hasConditionalLogic, hasWelcomeScreen, totalQuestionsForDisplay } = useFlow();
  const { session, clearSession, restartSession, addAnswer } = useSession();
  const history = useHistory();
  const { currentQuestionNumber } = useProgress();

  const [isRestarting, setRestarting] = useState(false);

  useEffect(() => {
    const sendHeightToParent = () => {
      const height = document.body.scrollHeight;
      if (height) {
        window?.parent?.postMessage(
          {
            action: "resize",
            frameHeight: height,
          },
          "*"
        );
      }
    };

    const images = document.querySelectorAll("img");
    images.forEach((image) => {
      if (image.complete) {
        sendHeightToParent();
      } else {
        image.addEventListener("load", sendHeightToParent);
        image.addEventListener("error", sendHeightToParent);
      }
    });
    window.addEventListener("load", sendHeightToParent);

    sendHeightToParent();

    const resizeObserver = new ResizeObserver(sendHeightToParent);
    resizeObserver.observe(document.body);

    return () => {
      resizeObserver.disconnect();
      images.forEach((image) => {
        image.removeEventListener("load", sendHeightToParent);
        image.removeEventListener("error", sendHeightToParent);
        window.removeEventListener("load", sendHeightToParent);
      });
    };
  }, []);

  const current = parseInt(number, 10);

  // map question index to question number
  const questionNumberMap = {} as { [key: number]: number };
  let questionNumber = 0;
  flow?.nodes
    ?.filter((node) => node?.type !== FlowNodeType.Welcome)
    ?.forEach((node, idx) => {
      if (node.type !== FlowNodeType.Transition && node.type !== FlowNodeType.Email) {
        questionNumberMap[idx + 1] = ++questionNumber;
      }
    });

  const question = useMemo(() => flow.nodes?.[current - (hasWelcomeScreen ? 0 : 1)], [flow, hasWelcomeScreen, current]);

  // add a function addLanternProperty to the window object
  useEffect(() => {
    if (!question) {
      return;
    }
    (window as any).addLanternProperty = async (propertyName, propertyValue) => {
      await addAnswer({
        nodeId: question.id,
        propertyName,
        propertyValue,
      });
    };
  }, [question]);

  const backLink = useMemo(() => {
    return history.goBack;
  }, [history]);

  const onReset = async () => {
    if (isRestarting) {
      return;
    }

    document.cookie = `lantern_${slug}_session_id=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;

    clearSession();
    history.replace("/");
    return;

    setRestarting(true);

    const success = await restartSession();

    if (!success) {
      clearSession();
    }

    history.replace("/");
  };

  (window as any).lantern = {
    ...(window as any)?.lantern,
    restartQuiz: onReset,
  };

  useEffect(() => {
    if (!session) {
      history.replace("/");
    }
  }, [session]);

  useEffect(() => {
    if (session?.status === SessionStatus.Completed) {
      onReset();
    }
  }, []);

  if (!session) {
    return null;
  }

  let questionID = "";
  if (question?.type === FlowNodeType.Transition) {
    questionID = "question-transition";
  } else if (question?.type === FlowNodeType.Email) {
    questionID = "question-email";
  } else if (current && questionNumberMap?.[current]) {
    questionID = `question-${questionNumberMap[current]}`;
  }

  return (
    <div className={questionID}>
      {flow.hasProgressBar && question?.type !== FlowNodeType.Transition && question?.type !== FlowNodeType.Email && (
        <m.div
          variants={{
            initial: { opacity: 0 },
            show: {
              opacity: 1,
              transition: { delay: 0.3, ...config.transition },
            },
            exit: {
              opacity: 0,
              transition: { delay: 0, ...config.transition },
            },
          }}
          initial="initial"
          animate="show"
          exit="exit"
        >
          <ProgressBar percentage={(questionNumberMap?.[current] ?? 0) * (100 / (totalQuestionsForDisplay ?? 1))} color={flow?.primaryColor as string} />
        </m.div>
      )}

      <AnimatePresence exitBeforeEnter>
        {question?.type !== FlowNodeType.Transition ? (
          <m.div
            key="progress"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { delay: 0.3 } }}
            exit={{ opacity: 0, transition: { delay: 0 } }}
            transition={config.transition}
          >
            <Flex flexDirection="row" alignItems="center" justifyContent="space-between" pt={3} mb={3}>
              {current !== 1 || flow?.nodes?.[0]?.type === FlowNodeType.Welcome ? <BackButton action={backLink} /> : <Box height="24px" />}

              {flow?.hasStepIndicator && question?.type !== FlowNodeType.Email && (
                <Text display={["block", "none"]} color="black40" fontSize={2} lineHeight="19px" dir="ltr">
                  {currentQuestionNumber && questionNumberMap?.[currentQuestionNumber]}
                  {!hasConditionalLogic && ` / ${totalQuestionsForDisplay}`}
                </Text>
              )}
            </Flex>
          </m.div>
        ) : (
          <m.div animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={config.transition} />
        )}
      </AnimatePresence>

      <m.div animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={config.transition}>
        <AnimatePresence exitBeforeEnter>
          {question?.type === FlowNodeType.Email ? (
            <EmailForm current={current} question={question} />
          ) : question?.type === FlowNodeType.InputOneLineText ? (
            <InputOneLineTextForm
              current={current}
              question={question}
              isLastQuestion={questionNumberMap?.[current] === totalQuestionsForDisplay}
              key={`question-${current}`}
            />
          ) : question?.type === FlowNodeType.InputMultiLineText ? (
            <InputMultiLineTextForm
              current={current}
              question={question}
              isLastQuestion={questionNumberMap?.[current] === totalQuestionsForDisplay}
              key={`question-${current}`}
            />
          ) : question?.type === FlowNodeType.Transition ? (
            <TransitionScreen
              current={current}
              question={question}
              isLastQuestion={questionNumberMap?.[current] === totalQuestionsForDisplay}
              key={`question-${current}`}
            />
          ) : (
            <QuestionForm
              current={current}
              question={question}
              isLastQuestion={questionNumberMap?.[current] === totalQuestionsForDisplay}
              key={`question-${current}`}
            />
          )}
        </AnimatePresence>
      </m.div>

      <div className="end-of-quiz"></div>
    </div>
  );
};

export default Question;
